import {
  collection,
  doc,
  addDoc,
  arrayRemove,
  arrayUnion,
  deleteDoc,
  increment,
  updateDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
  where
} from "firebase/firestore";
import { db } from "./firebase";
import type { Comment, Conversation, DirectMessage, ForumUser, Post, ReviewPost, Section, StoreItem, UserRole } from "./types";

export const POSTS_COLLECTION = "posts";
export const TOP_POSTS_COLLECTION = "topPosts";
export const POST_REVIEW_MODE_OPEN = "OPEN";
export const POST_REVIEW_MODE_CLOSED = "CLOSED";

export async function getSections(): Promise<Section[]> {
  const snapshot = await getDocs(collection(db, "sections"));
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() } as Section))
    .sort((a, b) => timeOf(b.createdAt) - timeOf(a.createdAt) || a.name.localeCompare(b.name));
}

export async function getFeedPosts(max = 40): Promise<Post[]> {
  const base = query(
    collection(db, POSTS_COLLECTION),
    where("reviewStatus", "==", "APPROVED"),
    orderBy("lastActivityTimestamp", "desc"),
    limit(max)
  );
  const snapshot = await getDocs(base);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as Post));
}

export async function getTopPosts(max = 20): Promise<Post[]> {
  const base = query(
    collection(db, TOP_POSTS_COLLECTION),
    where("reviewStatus", "==", "APPROVED"),
    orderBy("lastActivityTimestamp", "desc"),
    limit(max)
  );
  const snapshot = await getDocs(base);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as Post));
}

export async function getSectionPosts(sectionId: string): Promise<Post[]> {
  const snapshot = await getDocs(
    query(collection(db, POSTS_COLLECTION), where("sectionId", "==", sectionId))
  );
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() } as Post))
    .filter((post) => post.reviewStatus === "APPROVED")
    .filter((post) => !post.targetType || post.targetType === "section" || post.targetId === sectionId)
    .sort((a, b) => timeOf(b.lastActivityTimestamp) - timeOf(a.lastActivityTimestamp));
}

export async function getSection(sectionId: string): Promise<Section | null> {
  const snapshot = await getDoc(doc(db, "sections", sectionId));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as Section;
}

export async function getAllSearchPosts(): Promise<Array<{ post: Post; collectionName: string }>> {
  const [posts, topPosts] = await Promise.all([getFeedPosts(100), getTopPosts(50)]);
  return [
    ...posts.map((post) => ({ post, collectionName: POSTS_COLLECTION })),
    ...topPosts.map((post) => ({ post, collectionName: TOP_POSTS_COLLECTION }))
  ];
}

export async function getPost(collectionName: string, postId: string): Promise<Post | null> {
  const snapshot = await getDoc(doc(db, collectionName, postId));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as Post;
}

export async function getStoreItems(): Promise<StoreItem[]> {
  const snapshot = await getDocs(collection(db, "store"));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as StoreItem));
}

export async function buyStoreItem(user: ForumUser, item: StoreItem) {
  if (user.purchasedItems?.includes(item.id)) {
    throw new Error("You already own this item.");
  }
  if ((user.coins || 0) < (item.price || 0)) {
    throw new Error("Not enough coins.");
  }

  await updateDoc(doc(db, "users", user.uid), {
    coins: increment(-(item.price || 0)),
    purchasedItems: arrayUnion(item.id)
  });
}

export async function createStoreItem(input: { name: string; description: string; price: number; imageUrl?: string | null }) {
  const ref = doc(collection(db, "store"));
  const item = {
    id: ref.id,
    name: input.name,
    description: input.description,
    price: input.price,
    imageUrl: input.imageUrl || null
  };
  await setDoc(ref, item);
}

export async function updateStoreItem(
  itemId: string,
  input: { name: string; description: string; price: number; imageUrl?: string | null }
) {
  await updateDoc(doc(db, "store", itemId), {
    name: input.name,
    description: input.description,
    price: input.price,
    imageUrl: input.imageUrl || null
  });
}

export async function deleteStoreItem(itemId: string) {
  await deleteDoc(doc(db, "store", itemId));
}

export async function getComments(postId: string): Promise<Comment[]> {
  const snapshot = await getDocs(query(collection(db, "comments"), where("postId", "==", postId)));
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() } as Comment))
    .sort((a, b) => timeOf(a.timestamp) - timeOf(b.timestamp));
}

export async function addComment(post: Post, user: ForumUser, content: string) {
  await addDoc(collection(db, "comments"), {
    postId: post.id,
    sectionId: post.sectionId,
    authorId: user.uid,
    authorEmail: user.email || "",
    authorNickname: user.nickname || user.email || "",
    authorProfilePictureUrl: user.profilePictureUrl || "",
    authorRole: user.role || "USER",
    content,
    imageUrl: null,
    timestamp: serverTimestamp(),
    likes: [],
    likeCount: 0
  });
}

export async function createSectionPost(input: {
  sectionId: string;
  user: ForumUser;
  title: string;
  content: string;
  imageUrl?: string | null;
}) {
  const reviewMode = await getPostReviewMode();
  const role = normalizeRole(input.user.role);
  const needsReview = role === "USER" && reviewMode === POST_REVIEW_MODE_CLOSED;
  const postRef = doc(collection(db, POSTS_COLLECTION));
  const batch = writeBatch(db);

  batch.set(postRef, {
    id: postRef.id,
    sectionId: input.sectionId,
    targetId: input.sectionId,
    targetType: "section",
    authorId: input.user.uid,
    authorEmail: input.user.email || "",
    authorProfilePictureUrl: input.user.profilePictureUrl || "",
    authorRole: input.user.role || "USER",
    title: input.title,
    content: input.content,
    imageUrl: input.imageUrl || null,
    timestamp: serverTimestamp(),
    lastActivityTimestamp: serverTimestamp(),
    reviewStatus: needsReview ? "PENDING" : "APPROVED",
    likes: [],
    likeCount: 0,
    commentCount: 0,
    isLocked: false
  });

  if (!needsReview) {
    batch.update(doc(db, "users", input.user.uid), { coins: increment(5) });
  }

  await batch.commit();
  return { postId: postRef.id, needsReview };
}

export async function createSection(input: { name: string; description: string; imageUrl?: string | null; appHtml?: string }) {
  const ref = doc(collection(db, "sections"));
  await updateOrSetSection(ref.id, input);
}

export async function updateOrSetSection(
  sectionId: string,
  input: { name: string; description: string; imageUrl?: string | null; appHtml?: string }
) {
  const sectionRef = doc(db, "sections", sectionId);
  const snapshot = await getDoc(sectionRef);
  const data = {
    id: sectionId,
    name: input.name,
    description: input.description,
    imageUrl: input.imageUrl || null,
    appHtml: input.appHtml?.trim() || "",
    ...(snapshot.exists() ? {} : { createdAt: serverTimestamp() })
  };
  if (snapshot.exists()) await updateDoc(sectionRef, data);
  else await setDoc(sectionRef, data);
}

export async function deleteSection(sectionId: string) {
  await deleteDoc(doc(db, "sections", sectionId));
}

export async function updatePostLike(collectionName: string, post: Post, uid: string) {
  const liked = post.likes?.includes(uid) === true;
  const likes = liked ? post.likes?.filter((id) => id !== uid) || [] : [...(post.likes || []), uid];
  await updateDoc(doc(db, collectionName, post.id), {
    likes,
    likeCount: liked ? Math.max((post.likeCount || 0) - 1, 0) : (post.likeCount || 0) + 1
  });
}

export async function toggleBookmark(user: ForumUser, key: string) {
  const collectedPosts = user.collectedPosts?.includes(key)
    ? user.collectedPosts.filter((item) => item !== key)
    : [...(user.collectedPosts || []), key];
  await updateDoc(doc(db, "users", user.uid), { collectedPosts });
}

export async function toggleFollow(viewer: ForumUser, targetUserId: string) {
  if (viewer.uid === targetUserId) return;

  const isFollowing = viewer.following?.includes(targetUserId) === true;
  const batch = writeBatch(db);
  const viewerRef = doc(db, "users", viewer.uid);
  const targetRef = doc(db, "users", targetUserId);

  batch.update(viewerRef, {
    following: isFollowing ? arrayRemove(targetUserId) : arrayUnion(targetUserId)
  });
  batch.update(targetRef, {
    followers: isFollowing ? arrayRemove(viewer.uid) : arrayUnion(viewer.uid)
  });

  await batch.commit();
  return !isFollowing;
}

export async function getConversations(uid: string): Promise<Conversation[]> {
  const snapshot = await getDocs(query(collection(db, "conversations"), where("participants", "array-contains", uid)));
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() } as Conversation))
    .sort((a, b) => timeOf(b.lastMessageTimestamp) - timeOf(a.lastMessageTimestamp));
}

export async function getConversation(conversationId: string): Promise<Conversation | null> {
  const snapshot = await getDoc(doc(db, "conversations", conversationId));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() } as Conversation;
}

export async function getDirectMessages(conversationId: string): Promise<DirectMessage[]> {
  const snapshot = await getDocs(
    query(collection(db, "conversations", conversationId, "messages"), orderBy("timestamp", "asc"))
  );
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as DirectMessage));
}

export async function sendDirectMessage(input: {
  conversationId: string;
  senderId: string;
  receiverId: string;
  text: string;
}) {
  const messageRef = doc(collection(db, "conversations", input.conversationId, "messages"));
  await setDoc(doc(db, "conversations", input.conversationId), {
    id: input.conversationId,
    participants: [input.senderId, input.receiverId].sort(),
    lastMessage: input.text,
    lastMessageSenderId: input.senderId,
    lastMessageTimestamp: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  await setDoc(messageRef, {
    id: messageRef.id,
    conversationId: input.conversationId,
    senderId: input.senderId,
    receiverId: input.receiverId,
    text: input.text,
    selectedItemIconUrl: null,
    timestamp: serverTimestamp()
  });
}

export async function getUser(uid: string): Promise<ForumUser | null> {
  const snapshot = await getDoc(doc(db, "users", uid));
  if (!snapshot.exists()) return null;
  return { uid: snapshot.id, ...snapshot.data() } as ForumUser;
}

export async function getAllUsers(): Promise<ForumUser[]> {
  const snapshot = await getDocs(collection(db, "users"));
  return snapshot.docs
    .map((item) => ({ uid: item.id, ...item.data() } as ForumUser))
    .sort((a, b) => displayName(a).localeCompare(displayName(b)));
}

export async function updateUserRole(uid: string, role: UserRole) {
  await updateDoc(doc(db, "users", uid), { role });
}

export async function updateManagedSections(uid: string, managedSections: string[]) {
  await updateDoc(doc(db, "users", uid), { managedSections });
}

export async function getPostReviewMode(): Promise<string> {
  const snapshot = await getDoc(doc(db, "appSettings", "contentReview"));
  return snapshot.get("postReviewMode") || POST_REVIEW_MODE_CLOSED;
}

export async function setPostReviewMode(mode: string) {
  await setDoc(doc(db, "appSettings", "contentReview"), { postReviewMode: mode }, { merge: true });
}

export async function getPendingReviewPosts(): Promise<ReviewPost[]> {
  const [posts, topPosts] = await Promise.all([
    getDocs(query(collection(db, POSTS_COLLECTION), where("reviewStatus", "==", "PENDING"))),
    getDocs(query(collection(db, TOP_POSTS_COLLECTION), where("reviewStatus", "==", "PENDING")))
  ]);
  return [
    ...posts.docs.map((item) => ({ post: { id: item.id, ...item.data() } as Post, collectionName: POSTS_COLLECTION })),
    ...topPosts.docs.map((item) => ({
      post: { id: item.id, ...item.data() } as Post,
      collectionName: TOP_POSTS_COLLECTION
    }))
  ].sort((a, b) => timeOf(b.post.timestamp) - timeOf(a.post.timestamp));
}

export async function reviewPost(collectionName: string, postId: string, status: "APPROVED" | "REJECTED", reviewerId: string) {
  await updateDoc(doc(db, collectionName, postId), {
    reviewStatus: status,
    reviewedBy: reviewerId,
    reviewedAt: serverTimestamp()
  });
}

export async function getUsersByIds(uids: string[]): Promise<Record<string, ForumUser>> {
  const uniqueIds = [...new Set(uids.filter(Boolean))];
  if (!uniqueIds.length) return {};

  const entries = await Promise.all(
    uniqueIds.map(async (uid) => {
      const user = await getUser(uid);
      return user ? ([uid, user] as const) : null;
    })
  );

  return Object.fromEntries(entries.filter(Boolean) as Array<readonly [string, ForumUser]>);
}

export function conversationPathForUsers(firstUserId: string, secondUserId: string) {
  return `/inbox/${[firstUserId, secondUserId].sort().join("_")}`;
}

export function postPath(post: Post, collectionName = POSTS_COLLECTION) {
  return `/posts/${collectionName}/${encodeURIComponent(post.id)}`;
}

export function collectionKey(collectionName: string, postId: string) {
  return `${collectionName}:${postId}`;
}

export function timeOf(value: unknown): number {
  if (value && typeof value === "object" && "toMillis" in value && typeof value.toMillis === "function") {
    return value.toMillis();
  }
  return 0;
}

function displayName(user: ForumUser) {
  return user.nickname || user.email || user.uid;
}

function normalizeRole(role?: string): UserRole {
  const normalized = role?.toUpperCase();
  if (normalized === "ADMIN" || normalized === "COADMIN") return normalized;
  return "USER";
}
